import { NextPage } from 'next';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import Head from 'next/head';

const PrivacyPolicy: NextPage = () => {
  return (
    <>
      <Head>
        <title>kliggit - Privacy Policy</title>
      </Head>
      <div>
        <Header />
        <div className="px-5 md:px-[4.875rem] lg:px-[8.8125rem] xl:px-[14.375rem] mb-20">
          <h1 className="mb-4 font-semibold">Privacy Policy</h1>
          <main>
            <p className="mb-6 font-semibold">Last updated: July 14, 2021</p>
            <p className="mb-6">
              kliggit (&quot;us&quot;, &quot;we&quot;, or &quot;our&quot;)
              includes kliggit, Inc., our kliggit.com website and our mobile
              applications on any platform, and any related sites, services, and
              goods or any other site operated by kliggit linked to this Policy
              (collectively, the &quot;Site&quot;). By accessing or using this
              Site, you agree to all Terms, including this Privacy Policy. If
              you don&apos;t agree, you may not use or access this site. This
              page describes our policies regarding the collection, use, and
              disclosure of Personal Information we receive from users of the
              Site.
            </p>
            <ol className="ml-9 mb-[4.5rem] space-y-6 list-decimal list-inside">
              <li>
                We use your Personal Information only for providing and
                improving the Site. By using the Site, you agree to the
                collection and use of information under this policy.
              </li>
              <li>
                Information Collection and Use. While using our Site, you may
                provide Personal Information (information that can identify
                you).
                <ul className="mt-6 ml-9 space-y-6 list-inside list-lower-alpha">
                  <li>
                    This may include account login details: a username, a
                    password, a valid email address, telephone number (if
                    required), or other contact details.
                  </li>

                  <li>
                    Profile information, like a photograph, first and last name,
                    which allows other users to identify or find you. This also
                    includes any Content you have provided or posted to the
                    Site, including TalkTime or other videos, photos, comments,
                    hashtags, etc.
                  </li>

                  <li>
                    Other information may include details about other social
                    networks linked to our Site, details about your listed
                    TalkTimes, purchases, education, profession and expertise,
                    authentication information as permitted by law.
                  </li>

                  <li>
                    We also collect information about your communications with
                    kliggit and other Users.
                  </li>

                  <li>
                    We collect information when you use our Site, including
                    pages visited, purchases, and your interaction with other
                    Users. We also may collect information from third parties
                    about demographic and navigation data. We collect and use
                    this analytics information so it cannot reasonably be used
                    to identify any particular User.
                  </li>

                  <li>
                    We collect Log and other technological data. When you use
                    our Site, we collect information your browser or device
                    sends (&quot;Log Data&quot;). This Log Data may include
                    information such as your computer&apos;s Internet Protocol
                    (&quot;IP&quot;) address, browser type, browser version, and
                    the pages of our Site you visit, number of clicks, the time
                    and date of your visit, the time spent on those pages and
                    other statistics. We use this information to enhance user
                    experience, personalize your experience and monitor the Site
                    for fraud and inappropriate content or behavior.
                  </li>

                  <li>
                    Information about purchases. If you use our Site for
                    purchases or other financial transactions (like when you
                    purchase a TalkTime or donate for a kReserve), we collect
                    information about the purchase or transaction. Payment is
                    made through our third party payment provider which collects
                    your specific payment information (for example, credit or
                    debit card information). We do not collect or retain your
                    specific financial information. Any payments by us are made
                    through the same third party payment provider.
                  </li>

                  <li>
                    We work with third parties who help us provide offerings and
                    services and improve our Site. We sell none of your Personal
                    Information to anyone. We provide information and content to
                    vendors and service providers who support our Site. We
                    provide aggregated statistics and analysis to help people
                    and businesses understand how Users are engaging with our
                    Site.
                  </li>

                  <li>
                    We may provide advertisers with similar aggregated
                    statistics or demographic data about how their ads are
                    performing, but we don&apos;t share information that
                    personally identifies you unless you give us permission.
                  </li>

                  <li>
                    As outlined in our Terms of Service and Use, we share
                    information with law enforcement or in response to legal
                    requests in certain circumstances.
                  </li>
                </ul>
              </li>
              <li>
                Information Retention. Generally, we keep Personal Information
                only for as long as needed for the purpose it was collected. We
                retain Personal Information if it is necessary and relevant for
                our operations. We keep Personal Information from closed
                accounts to comply with laws, prevent fraud, collect any fees
                owed, resolve disputes, assist with any investigation, enforce
                our Site terms, and take other actions as permitted by law.
              </li>
              <li>
                Communications. We may use your Personal Information to contact
                you. We may send you marketing communications, communicate with
                you about our offerings or Site services, and let you know about
                our policies and terms. We also use your Personal Information to
                respond to you when you contact us.
              </li>
              <li>
                Security. The security of your Personal Information is important
                to us. No method of transmission over the Internet, or method of
                electronic storage, is 100% secure. We use commercially
                acceptable methods to protect your Personal Information, but
                cannot guarantee its absolute security.
              </li>
              <li>
                Children’s Information. Our Site is intended for the general
                public and not for use by anyone under the age of 13 in the
                United States (or under the applicable legal age outside of the
                United States). We do not knowingly collect Personal Information
                from children younger than the age of 13 (or under the
                applicable legal age outside of the United States), without the
                consent of a parent or legal guardian. If we learn that we have
                done so, we will promptly remove it from our active Site and
                databases. If you are a parent or legal guardian and believe we
                have collected or used your child’s Personal Information without
                your consent, please contact us at
                <a href="mailto:privacy@kliggit.com">privacy@kliggit.com</a>.
              </li>
            </ol>
            <h4 className="mb-3 font-semibold">
              Changes to This Privacy Policy
            </h4>
            <p className="mb-[3.75rem]">
              This Privacy Policy is effective as of July 14, 2021 and will
              remain in effect until modified. Any changes are effective
              immediately after being posted on this page. We reserve the right
              to update or change our Privacy Policy at any time. Your continued
              use of the Site after we post any changes to this page will
              constitute your acknowledgement and consent to be bound by the
              updated Privacy Policy. If we change this Privacy Policy, we will
              notify you either through the email address you have provided us,
              or by placing a prominent notice on our Site.
            </p>
            <h4 className="mb-3 font-semibold">Contact Us</h4>
            <p>
              If you have questions about this Privacy Policy, please contact us
              at <a href="mailto:privacy@kliggit.com">privacy@kliggit.com</a>.
            </p>
          </main>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default PrivacyPolicy;
